/* ResourcedEmojiComponent.tsx generated by @compiled/babel-plugin v2.0.0 */
import { ax, ix } from "@compiled/react/runtime";
require("@compiled/webpack-loader/css-loader!@compiled/webpack-loader/css-loader/compiled-css.css?style=._3iDTPbcK7c%7Bdisplay%3Acontents%7D");
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { StrictXCSSProp } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import type { EmojiProvider } from '../../api/EmojiResource';
import type { EmojiLoadSuccessCallback, EmojiLoadFailCallback } from '../../api/EmojiUtils';
import { EmojiCommonProvider } from '../../context/EmojiCommonProvider';
import { type EmojiId, type EmojiProviderLookupOrder, type OptionalEmojiDescription, UfoEmojiTimings } from '../../types';
import { hasUfoMarked } from '../../util/analytics/hasUfoMarked';
import { sampledUfoRenderedEmoji } from '../../util/analytics/sampledUfoRenderedEmoji';
import { defaultEmojiHeight } from '../../util/constants';
import { emojiIdToEmoji } from '../../util/emojiIdToEmoji';
import { isImageRepresentation } from '../../util/is-image-representation';
import { isMediaRepresentation } from '../../util/is-media-representation';
import { isPromise } from '../../util/is-promise';
import Emoji from './Emoji';
import EmojiPlaceholder from './EmojiPlaceholder';
export interface BaseResourcedEmojiProps {
  /**
   * Custom Fallback allows a custom element or string to be rendered if an emoji fails to be fetched or found.
   * By default it takes the fallback or shortName inside emojiId, but if this prop is set it override the internal
   * fallbacks
   * customFallback<Element | string> else emojiId.fallback else emojiId.shortName.
   * Defaults to `undefined`.
   */
  customFallback?: JSX.Element | string;
  /**
   * This should only be set when the emoji is being used in the Editor.
   * Currently when set -- this prevents any aria labels being added.
   * This is acceptable in Editor -- as it uses another technique to announce the emoji nodes.
   */
  editorEmoji?: true;
  /**
   * Emoji to display
   */
  emojiId: EmojiId;
  /**
   * Preferred emoji provider type order when resolving shortName-only emoji.
   * The first matching type wins.
   */
  emojiProviderLookupOrder?: EmojiProviderLookupOrder;
  /**
   * Scales the emoji proportionally to provided hight.
   * Defaults to `undefined`.
   */
  fitToHeight?: number;
  /**
   * Ignores the SSR wrapper when laying out the emoji.
   * Defaults to `false`.
   */
  ignoreWrapper?: boolean;
  /**
   * Optimistic will call the fetch interface first and not wait for the entire emoji collection
   * to be available before rendering. This is useful for views or pages that show a select set of
   * emojis.
   * Defaults to `false`.
   */
  optimistic?: boolean;
  /**
   * Will attempt to render a highly condensed version of the emoji with an image url before showing the meta version.
   * All that is required for optimistic images to render is an emojiId, imageUrl and sizing props.
   * Defaults to `undefined`.
   */
  optimisticImageURL?: string;
  /**
   * Indicates that this emoji is being rendered in a page title context.
   * This is used to adjust certain behaviors to reduce TTVC issues.
   * Defaults to `false`.
   */
  pageTitleEmoji?: boolean;

  /**
   * allows custom styling to the placeholder component while the emoji is loading.
   */
  placeholderXcss?: StrictXCSSProp<'backgroundColor', never>;

  /**
   * Renders unicode emoji through an image representation when a fixed height is supplied.
   * Defaults to `true`.
   */
  renderUnicodeEmojiAsImage?: boolean;

  /**
   * Allows to show the tooltip.
   * Defaults to `false`.
   */
  showTooltip?: boolean;
}
export interface Props extends BaseResourcedEmojiProps {
  /**
   * EmojiResource instance that handles fetching of emoji data.
   */
  emojiProvider: Promise<EmojiProvider>;

  /**
   * A callback triggered on emoji load failure
   */
  onEmojiLoadFail?: EmojiLoadFailCallback;

  /**
   * A callback triggered on emoji load success
   */
  onEmojiLoadSuccess?: EmojiLoadSuccessCallback;
}
const ignoreWrapperStyles = null;
enum ResourcedEmojiComponentRenderStatesEnum {
  INITIAL = 'INITIAL',
  FALLBACK = 'FALLBACK',
  EMOJI = 'EMOJI',
}
export const ResourcedEmojiComponent = ({
  emojiProvider,
  emojiId,
  emojiProviderLookupOrder,
  showTooltip = false,
  customFallback = undefined,
  fitToHeight = defaultEmojiHeight,
  ignoreWrapper = false,
  optimistic = false,
  optimisticImageURL = undefined,
  editorEmoji,
  renderUnicodeEmojiAsImage = true,
  pageTitleEmoji = false,
  placeholderXcss,
  onEmojiLoadSuccess,
  onEmojiLoadFail
}: Props): React.JSX.Element => {
  const {
    shortName,
    id,
    fallback
  } = emojiId;
  const [emoji, setEmoji] = useState<OptionalEmojiDescription>();
  const [loaded, setLoaded] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [resolvedEmojiProvider, setResolvedEmojiProvider] = useState<EmojiProvider>();
  const fetchOrGetEmoji = useCallback(async (_emojiProvider: EmojiProvider, emojiId: EmojiId, optimisticFetch: boolean = false) => {
    if (!_emojiProvider.fetchByEmojiId) {
      setEmoji(undefined);
      const reason = 'missing fetchByEmojiId interface';
      onEmojiLoadFail && onEmojiLoadFail(emojiId.id, reason);
      sampledUfoRenderedEmoji(emojiId).failure({
        metadata: {
          reason,
          source: 'ResourcedEmojiComponent'
        }
      });
    }
    const useUnicodeEmojis = expValEqualsNoExposure('platform_use_unicode_emojis', 'isEnabled', true);
    const normalizedEmojiId = useUnicodeEmojis ? {
      ...emojiId,
      id: emojiId.id || undefined
    } : emojiId;
    // A shortName-only optimistic request can resolve without an emoji before the catalogue loads.
    // Use the non-optimistic path so it waits for the catalogue and tokenizes custom emoji media.
    const shouldFetchOptimistically = useUnicodeEmojis ? optimisticFetch && Boolean(emojiId.id) : optimisticFetch;
    const foundEmoji = _emojiProvider.fetchByEmojiId(normalizedEmojiId, shouldFetchOptimistically, fg('platform_bitbucket_fix_shortname_and_ordering') ? emojiProviderLookupOrder : undefined);
    sampledUfoRenderedEmoji(emojiId).mark(UfoEmojiTimings.METADATA_START);
    if (isPromise<OptionalEmojiDescription>(foundEmoji)) {
      setLoaded(false);
      foundEmoji.then(emoji => {
        setEmoji(emoji);
        if (!emoji) {
          const reason = 'failed to find';
          onEmojiLoadFail && onEmojiLoadFail(emojiId.id, reason);
          // emoji is undefined
          sampledUfoRenderedEmoji(emojiId).failure({
            metadata: {
              reason,
              source: 'ResourcedEmojiComponent',
              data: {
                emoji: {
                  id: emojiId.id,
                  shortName: emojiId.shortName
                }
              }
            }
          });
        }
      }).catch(() => {
        setEmoji(undefined);
        const reason = 'failed to load';
        onEmojiLoadFail && onEmojiLoadFail(emojiId.id, reason);
        sampledUfoRenderedEmoji(emojiId).failure({
          metadata: {
            reason,
            source: 'ResourcedEmojiComponent',
            data: {
              emoji: {
                id: emojiId.id,
                shortName: emojiId.shortName
              }
            }
          }
        });
      }).finally(() => {
        setLoaded(true);
        sampledUfoRenderedEmoji(emojiId).mark(UfoEmojiTimings.METADATA_END);
      });
    } else {
      setEmoji(foundEmoji);
      setLoaded(true);
      sampledUfoRenderedEmoji(emojiId).mark(UfoEmojiTimings.METADATA_END);
    }
  }, [emojiProviderLookupOrder, onEmojiLoadFail]);
  useEffect(() => {
    if (!emojiId) {
      return;
    }
    if (!hasUfoMarked(sampledUfoRenderedEmoji(emojiId), UfoEmojiTimings.FMP_END)) {
      sampledUfoRenderedEmoji(emojiId).markFMP();
    }
  }, [emojiId]);
  useMemo(() => {
    if (!resolvedEmojiProvider || !emojiId) {
      return;
    }
    fetchOrGetEmoji(resolvedEmojiProvider, emojiId, optimistic);
  }, [resolvedEmojiProvider, emojiId, optimistic, fetchOrGetEmoji]);

  /**
   * Setting resolved emoji provider for optimistic rendering
   */
  useEffect(() => {
    Promise.resolve(emojiProvider).then(emojiProvider => {
      setResolvedEmojiProvider(emojiProvider);
    });
  }, [emojiProvider]);
  const emojiRenderState = useMemo<ResourcedEmojiComponentRenderStatesEnum>(() => {
    if (!emoji && !loaded && !optimisticImageURL || optimisticImageURL && !emoji && !id && expValEqualsNoExposure('platform_use_unicode_emojis', 'isEnabled', true)) {
      return ResourcedEmojiComponentRenderStatesEnum.INITIAL;
    } else if (!emoji && loaded || imageLoadError) {
      return ResourcedEmojiComponentRenderStatesEnum.FALLBACK;
    }
    return ResourcedEmojiComponentRenderStatesEnum.EMOJI;
  }, [emoji, loaded, optimisticImageURL, imageLoadError, id]);
  const optimisticEmojiDescription = useMemo(() => {
    // For STANDARD emojis, use native unicode character instead of optimistic image.
    if (expValEqualsNoExposure('platform_use_unicode_emojis', 'isEnabled', true)) {
      const resolvedId = id || emoji?.id;
      if (!resolvedId) return undefined;
      const unicodeEmoji = emojiIdToEmoji(resolvedId);
      if (unicodeEmoji) {
        return {
          ...(emoji ?? {
            id,
            shortName,
            fallback,
            type: 'STANDARD',
            category: '',
            searchable: true
          }),
          representation: {
            unicodeEmoji
          }
        };
      }
    }

    // reduce blast radius by targeting page title
    if (pageTitleEmoji && optimisticImageURL && fg('platform_emoji_prevent_img_src_changing')) {
      return {
        id,
        shortName,
        fallback,
        type: '',
        category: '',
        searchable: true,
        representation: {
          height: fitToHeight || defaultEmojiHeight,
          width: fitToHeight || defaultEmojiHeight,
          imagePath: optimisticImageURL
        }
      };
    }
    // extend prevention to all surfaces
    if (optimisticImageURL && fg('platform_emoji_prevent_img_src_changing_all')) {
      return {
        id,
        shortName,
        fallback,
        type: '',
        category: '',
        searchable: true,
        representation: {
          height: fitToHeight || defaultEmojiHeight,
          width: fitToHeight || defaultEmojiHeight,
          imagePath: optimisticImageURL
        }
      };
    }
    if (optimisticImageURL) {
      if (emoji && (isImageRepresentation(emoji.representation) || isMediaRepresentation(emoji?.representation))) {
        const {
          width,
          height
        } = emoji.representation;
        return {
          ...emoji,
          representation: {
            width,
            height,
            imagePath: optimisticImageURL
          }
        };
      } else {
        return {
          id,
          shortName,
          fallback,
          type: '',
          category: '',
          representation: {
            height: fitToHeight || defaultEmojiHeight,
            width: fitToHeight || defaultEmojiHeight,
            imagePath: optimisticImageURL
          },
          searchable: true
        };
      }
    }
    return emoji;
  }, [emoji, optimisticImageURL, fallback, fitToHeight, id, shortName, pageTitleEmoji]);
  const handleOnLoadError = useCallback((emojiId: EmojiId) => {
    setImageLoadError(true);
    const reason = 'load error';
    onEmojiLoadFail && onEmojiLoadFail(emojiId.id, reason);
    sampledUfoRenderedEmoji(emojiId).failure({
      metadata: {
        reason,
        source: 'ResourcedEmojiComponent',
        emojiId: emojiId.id
      }
    });
  }, [onEmojiLoadFail]);
  const handleOnLoadSuccess = useCallback((emojiId: EmojiId) => {
    onEmojiLoadSuccess && onEmojiLoadSuccess(emojiId.id);
  }, [onEmojiLoadSuccess]);

  // use width: auto when fitToHeight is defined
  const autoWidth = !!fitToHeight;
  return <EmojiCommonProvider emojiProvider={resolvedEmojiProvider}>
			<span data-emoji-id={id} data-emoji-short-name={shortName} data-emoji-text={fallback || shortName} data-ssr-placeholder={`emoji-${id ?? shortName}`} data-ssr-placeholder-replace={`emoji-${id ?? shortName}`} className={ax([ignoreWrapper && "_3iDTPbcK7c"])}>
				{emojiRenderState === ResourcedEmojiComponentRenderStatesEnum.INITIAL && <EmojiPlaceholder shortName={shortName} showTooltip={showTooltip} size={fitToHeight || defaultEmojiHeight} loading xcss={placeholderXcss} />}
				{emojiRenderState === ResourcedEmojiComponentRenderStatesEnum.FALLBACK && <>{customFallback || fallback || shortName}</>}
				{emojiRenderState === ResourcedEmojiComponentRenderStatesEnum.EMOJI && optimisticEmojiDescription && <Emoji emoji={optimisticEmojiDescription} onLoadError={handleOnLoadError} onLoadSuccess={handleOnLoadSuccess} showTooltip={showTooltip} fitToHeight={fitToHeight} autoWidth={autoWidth} editorEmoji={editorEmoji} renderUnicodeEmojiAsImage={renderUnicodeEmojiAsImage} />}
			</span>
		</EmojiCommonProvider>;
};
export default ResourcedEmojiComponent;